import type { I18n } from "@lingui/core";

function isDigitChar(value: string): boolean {
  return value >= "0" && value <= "9";
}

function isIsoDatePrefix(value: string): boolean {
  if (value.length < 10) return false;
  const y0 = value.charAt(0);
  const y1 = value.charAt(1);
  const y2 = value.charAt(2);
  const y3 = value.charAt(3);
  const d0 = value.charAt(5);
  const d1 = value.charAt(6);
  const d2 = value.charAt(8);
  const d3 = value.charAt(9);
  if (
    !isDigitChar(y0) ||
    !isDigitChar(y1) ||
    !isDigitChar(y2) ||
    !isDigitChar(y3) ||
    !isDigitChar(d0) ||
    !isDigitChar(d1) ||
    !isDigitChar(d2) ||
    !isDigitChar(d3)
  ) {
    return false;
  }
  return value.charAt(4) === "-" && value.charAt(7) === "-";
}

export function isIsoDateLike(value: unknown): value is string {
  if (typeof value !== "string") return false;
  return isIsoDatePrefix(value.trim());
}

function toDateInput(value: string): string {
  return value.trim().slice(0, 10);
}

export function formatVizAxisDateLabel(value: string, i18n: I18n): string {
  if (!isIsoDateLike(value)) return value;
  try {
    return i18n.date(toDateInput(value), { month: "short", day: "numeric" });
  } catch {
    return value;
  }
}

export function formatVizTooltipDateLabel(value: string, i18n: I18n): string {
  if (!isIsoDateLike(value)) return value;
  try {
    return i18n.date(toDateInput(value), {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return value;
  }
}

/**
 * tooltip の軸ラベル値を locale-aware に整形する。
 *
 * @remarks
 * AI 可視化は date 軸だけでなく number/string 軸も取りうるため、
 * 呼び出し側ごとに `Date` 判定や数値整形を重複させずここで吸収する。
 * 解釈不能な値は隠さず、そのまま文字列化して tooltip に残す。
 */
export function formatVizTooltipDomainLabel(
  value: unknown,
  i18n: I18n,
): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return "";
    }

    return isIsoDateLike(trimmed)
      ? formatVizTooltipDateLabel(trimmed, i18n)
      : trimmed;
  }

  if (typeof value === "number") {
    return new Intl.NumberFormat(i18n.locale, {
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  }

  if (typeof value === "bigint") {
    return new Intl.NumberFormat(i18n.locale).format(value);
  }

  if (value instanceof Date) {
    try {
      return i18n.date(value, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return value.toISOString();
    }
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
}
